import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getRedirects, deleteRedirect } from './api';
import { useDomain } from './DomainContext';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import SiteSectionNav from './ui/SiteSectionNav';

const STATUS_BADGE = { 301: 'success', 302: 'warn', 410: 'danger' };

// For React Router to properly handle IDs with encoded slashes, we need to
// double-encode: React Router decodes once, leaving the API-encoded ID.
const encodeForRouter = (redirectId) => {
  if (redirectId.includes('/')) return encodeURIComponent(encodeURIComponent(redirectId));
  if (redirectId.includes('%2F')) return encodeURIComponent(redirectId);
  return redirectId;
};

const DomainRedirects = () => {
  const [redirects, setRedirects] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { domains, selectedDomain } = useDomain();

  const domain = selectedDomain?.id === domainId
    ? selectedDomain
    : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.title || '';

  const handleRedirectClick = (redirectId) => {
    navigate(`/domain/${domainId}/redirects/${encodeForRouter(redirectId)}/edit`);
  };

  const confirmDelete = async () => {
    const redirectId = pendingDelete;
    setPendingDelete(null);
    if (!redirectId) return;
    try {
      await deleteRedirect(domainId, redirectId);
      setRedirects((prev) => prev.filter((redirect) => redirect.id !== redirectId));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to delete redirect');
      }
    }
  };

  useEffect(() => {
    const fetchRedirects = async () => {
      try {
        const redirectsData = await getRedirects(domainId);
        setRedirects(redirectsData);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch redirects');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRedirects();
  }, [domainId, navigate]);

  const filteredRedirects = redirects.filter((redirect) =>
    Object.values(redirect).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const newRedirectPath = `/domain/${domainId}/redirects/new/edit`;

  return (
    <div className="page">
      <PageHeader
        title={hostname ? `Redirects · ${hostname}` : 'Redirects'}
        subtitle="Send old URLs to their new home, or mark them gone"
        actions={(
          <>
            <Link to={`/domain/${domainId}`} className="btn btn-secondary">← Back to domain</Link>
            <Link to={newRedirectPath} className="btn btn-primary">+ New redirect</Link>
          </>
        )}
      />
      <SiteSectionNav section="settings" />

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {loading ? (
        <Spinner label="Loading redirects…" />
      ) : redirects.length === 0 ? (
        <EmptyState
          title="No redirects yet"
          message="Redirects send visitors from a retired URL to its replacement."
          action={<Link to={newRedirectPath} className="btn btn-primary">+ New redirect</Link>}
        />
      ) : (
        <>
          <div className="toolbar">
            <input
              type="search"
              className="input grow"
              placeholder="Filter redirects…"
              aria-label="Filter redirects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="muted small">
              {filteredRedirects.length} of {redirects.length}
            </span>
          </div>

          {filteredRedirects.length === 0 ? (
            <EmptyState title="No matches" message="No redirect matches that filter." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Source path</th>
                    <th>Target path</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRedirects.map((redirect) => {
                    const source = decodeURIComponent(redirect.id);
                    return (
                      <tr
                        key={redirect.id}
                        className="clickable"
                        onClick={() => handleRedirectClick(redirect.id)}
                      >
                        <td className="mono" style={{ wordBreak: 'break-all' }}>{source}</td>
                        <td className="mono">
                          {redirect.is_gone ? <span className="muted">— gone —</span> : redirect.target_path}
                        </td>
                        <td>
                          <Badge state={STATUS_BADGE[redirect.status_code] || undefined}>
                            {redirect.status_code}{redirect.is_gone ? ' Gone' : ''}
                          </Badge>
                        </td>
                        <td className="muted">
                          {redirect.created_at ? new Date(redirect.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="muted">
                          {redirect.updated_at ? new Date(redirect.updated_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={(e) => { e.stopPropagation(); handleRedirectClick(redirect.id); }}
                          >
                            Edit
                          </button>
                          {domain?.hostname && (
                            <a
                              href={`https://${domain.hostname}/${source}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary btn-sm"
                              onClick={(e) => e.stopPropagation()}
                              title="Open the source URL in a new tab"
                            >
                              Test
                            </a>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-danger-text btn-sm"
                            onClick={(e) => { e.stopPropagation(); setPendingDelete(redirect.id); }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this redirect?"
        message={pendingDelete ? `Visitors to /${decodeURIComponent(pendingDelete)} will get a 404 once this is removed.` : ''}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default DomainRedirects;
